import { Description, Field } from "@headlessui/react";
import { useEffect, useRef, useState } from "react";
import { Button, Modal, TextInput, Typography } from "@/components/atoms";

const FLIGHT_NUMBER_MAX_LENGTH = 8;

/**
 * Clients type the digits alone ("123"), which the flight lookup on the date screen can't resolve — it
 * needs the airline code. A reminder only: nothing here validates the format, because the real check is
 * the server lookup a step later and this modal can't judge a number it never looks up.
 */
const PREFIX_HINT = "Include the airline code at the start, not just the number.";

interface FlightDetailsModalProps {
  open: boolean;
  /** Pre-fills the input when reopening for a pickup that already has a flight number. */
  initialValue?: string;
  /** Continue — commit the entered flight number. */
  onConfirm: (flightNumber: string) => void;
  /**
   * Not Flying — the DELIBERATE press. On create it is a plain dismiss (flight is optional); on an
   * existing flight booking it means "this is no longer a flight booking" and the caller clears the
   * captured flight.
   */
  onNotFlying: () => void;
  /**
   * Backdrop, Escape, or any other incidental close. Split from `onNotFlying` because that prop is
   * destructive on edit: wired to the dialog's `onClose`, an Escape keypress would silently throw away a
   * flight number the client never intended to drop. Defaults to `onNotFlying`, which is right for a
   * caller with nothing to clear — the create path.
   */
  onDismiss?: () => void;
  /**
   * Which leg is being captured (DEV-1487). Only the description changes: the arrival copy promises to
   * align the pickup with the flight's ARRIVAL, which is the opposite of what a drop-off booking does
   * with the number — it works backwards from the departure to a suggested pickup. Defaults to the
   * pickup so every existing call site is unchanged.
   */
  target?: "pickup" | "destination";
}

/**
 * Input-only Flight Details modal: captures a free-text flight number for an airport pickup so the
 * pickup can later be aligned to the flight's arrival. No flight lookup happens here — the flight is
 * resolved to details later, on the date screen.
 */
const DESCRIPTIONS: Record<NonNullable<FlightDetailsModalProps["target"]>, string> = {
  pickup:
    "Provide the flight number so we can schedule your pickup accordingly. You'll have the option to align the pickup time with the flight's arrival.",
  destination:
    "Provide the flight number so we can schedule your pickup accordingly. We'll suggest a pickup time that gets you to your terminal in time for departure.",
};

export const FlightDetailsModal = ({
  open,
  initialValue = "",
  onConfirm,
  onNotFlying,
  onDismiss,
  target = "pickup",
}: FlightDetailsModalProps) => {
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) setValue(initialValue);
  }, [open, initialValue]);

  const trimmed = value.trim();

  // Drop focus from the flight-number field before closing (Continue / Not Flying / backdrop) so
  // the input doesn't stay focused (and the mobile keyboard doesn't linger) after the modal hides.
  const handleNotFlying = () => {
    inputRef.current?.blur();
    onNotFlying();
  };
  const handleDismiss = () => {
    inputRef.current?.blur();
    (onDismiss ?? onNotFlying)();
  };
  const handleConfirm = () => {
    inputRef.current?.blur();
    onConfirm(trimmed.toUpperCase());
  };

  return (
    <Modal
      open={open}
      onClose={handleDismiss}
      variant="web-small"
      title="Flight Details"
      description={DESCRIPTIONS[target]}
      renderButtons={() => (
        <>
          <Button variant="tertiary" size="md" onClick={handleNotFlying}>
            Not Flying
          </Button>
          <Button variant="primary" size="md" disabled={!trimmed} onClick={handleConfirm}>
            Continue
          </Button>
        </>
      )}
    >
      {/* Headless UI's Input owns aria-describedby, so a hand-rolled one is dropped. Field + Description
          is the seam it does honour, and it keeps the wiring correct with both legs mounted at once. */}
      <Field>
        <TextInput
          ref={inputRef}
          iconName="Airplane"
          placeholder="Enter Flight Number (e.g. QF123)"
          maxLength={FLIGHT_NUMBER_MAX_LENGTH}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoFocus
        />
        <Description as={Typography} variant="paragraph" className="mt-1 leading-5 text-neutral-silver-gray">
          {PREFIX_HINT}
        </Description>
      </Field>
    </Modal>
  );
};
